import { useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { CalendarIcon, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Filter-item shape the rebuilt component reads from and writes back to.
 * Extra fields (id/field/operator from the host DataGrid's GridFilterItem)
 * flow through untouched via the spread in `applyValue({ ...item, value })`.
 *
 * `TExtra` lets callers narrow what else they carry on the item without us
 * importing any DataGrid library type — keeping the file MUI-free (zkolv
 * Unit 1.IMPL exit criteria).
 */
export type DateFilterItem<
    TExtra extends Record<string, unknown> = Record<string, unknown>,
> = TExtra & { value: string | null };

export interface DateFilterInputProps<
    TExtra extends Record<string, unknown> = Record<string, unknown>,
> {
    item: DateFilterItem<TExtra>;
    applyValue: (next: DateFilterItem<TExtra>) => void;
}

/**
 * Single-operator (`dateIs`) date filter input. Rebuilt from MUI's DatePicker
 * onto shadcn Popover + Calendar. The exported function signature stays a
 * structural superset of MUI's `GridFilterInputValueProps` so the 6 existing
 * `InputComponent: DateFilterInput` callers (DocumentList, OrganizationClients,
 * UsersTable, ClauseLibraryTable, …) work unchanged.
 *
 * Trigger shows "Select date" or the formatted `MMM d, yyyy` value; the
 * calendar opens via shadcn Popover. Selection commits a `yyyy-MM-dd` string
 * to `applyValue` (preserving any extra item fields via spread). The Clear
 * affordance appears only when a value is set.
 *
 * @example MUI DataGrid filterOperators wiring (host-passed item shape)
 * ```ts
 * filterOperators: [
 *   {
 *     value: "dateIs",
 *     InputComponent: DateFilterInput,
 *     getApplyFilterFn: ...,
 *   },
 * ];
 * ```
 *
 * @example Direct controlled use
 * ```tsx
 * const [item, setItem] = useState({ value: null });
 * <DateFilterInput item={item} applyValue={setItem} />
 * ```
 */

const PLACEHOLDER = "Select date";

function parse(value: string | null): Date | undefined {
    if (!value) return undefined;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : undefined;
}

function formatLabel(value: string | null): string {
    const parsed = parse(value);
    return parsed ? format(parsed, "MMM d, yyyy") : PLACEHOLDER;
}

export function DateFilterInput<
    TExtra extends Record<string, unknown> = Record<string, unknown>,
>({ item, applyValue }: DateFilterInputProps<TExtra>) {
    const [open, setOpen] = useState(false);
    const selected = parse(item.value);
    const hasValue = item.value !== null && item.value !== "";

    const handleSelect = (date: Date | undefined) => {
        if (!date) return;
        applyValue({ ...item, value: format(date, "yyyy-MM-dd") });
        setOpen(false);
    };

    const handleClear = () => {
        applyValue({ ...item, value: null });
    };

    return (
        <div className="inline-flex items-center gap-1">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger
                    render={
                        <Button
                            variant="outline"
                            size="sm"
                            aria-label={
                                hasValue ? formatLabel(item.value) : PLACEHOLDER
                            }
                            className={cn(
                                "justify-start gap-2 font-normal",
                                !hasValue && "text-muted-foreground"
                            )}
                        >
                            <CalendarIcon className="size-4" aria-hidden />
                            <span>
                                {hasValue
                                    ? formatLabel(item.value)
                                    : PLACEHOLDER}
                            </span>
                        </Button>
                    }
                />
                <PopoverContent
                    align="start"
                    sideOffset={4}
                    className="w-auto p-0"
                >
                    <Calendar
                        mode="single"
                        selected={selected}
                        onSelect={handleSelect}
                        defaultMonth={selected}
                        autoFocus
                    />
                </PopoverContent>
            </Popover>
            {hasValue && (
                <button
                    type="button"
                    aria-label="Clear date"
                    onClick={handleClear}
                    className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md",
                        "text-muted-foreground hover:text-foreground hover:bg-muted",
                        "focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2"
                    )}
                >
                    <X size={14} aria-hidden />
                </button>
            )}
        </div>
    );
}
